'use client';

import { useEffect, useMemo, useState } from 'react';
import { Crown, Loader2, Search, Users } from 'lucide-react';
import api from '@/app/lib/axios';
import { useAuth } from '@/app/hooks/context/AuthContext';
import { CommunityRaffle, RafflePlayer } from '@/types/raffle';
import { Label } from '@/app/raffles/components/RaffleBits';

// Show the search box only once the list is long enough to need it.
const SEARCH_FROM = 8;

/** Who entered and with how many tickets. Refetches whenever the ticket total moves. */
export default function EntriesList({ raffle }: { raffle: CommunityRaffle }) {
    const { user } = useAuth();
    const [entries, setEntries] = useState<RafflePlayer[] | null>(null);
    const [query, setQuery] = useState('');
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.get<{ players: RafflePlayer[] }>(`raffles/${raffle.id}/players`)
            .then(({ data }) => {
                if (cancelled) return;
                setEntries(data.players);
                setFailed(false);
            })
            .catch((error) => {
                console.error('Error fetching raffle entries:', error);
                // Keep a previously loaded list on a failed refresh; only flag it when there's nothing to show.
                if (!cancelled) setFailed(true);
            });
        return () => { cancelled = true; };
    }, [raffle.id, raffle.total_tickets]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const ranked = (entries ?? []).map((p, i) => ({ ...p, rank: i + 1 }));
        return q ? ranked.filter(p => p.username?.toLowerCase().includes(q)) : ranked;
    }, [entries, query]);

    const total = raffle.total_tickets || 1;

    return (
        <div className="flex flex-col min-w-0">
            <div className="flex items-center justify-between gap-2 mb-2">
                <Label>Entries · {raffle.participants.toLocaleString()}</Label>
                <Label>Tickets</Label>
            </div>

            {(entries?.length ?? 0) >= SEARCH_FROM && (
                <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search username"
                        className="w-full rounded-lg bg-gray-800/60 py-2 pl-8 pr-3 text-xs font-mono text-gray-200 placeholder-gray-500 outline-none focus:ring-2 focus:ring-emerald-500/40"
                    />
                </div>
            )}

            {entries === null && failed ? (
                <p className="rounded-xl bg-gray-800/30 py-10 text-center text-xs font-mono text-gray-400">Couldn&apos;t load entries right now.</p>
            ) : entries === null ? (
                <div className="flex items-center justify-center gap-2 py-10 text-xs font-mono text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading entries…
                </div>
            ) : entries.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 rounded-xl bg-gray-800/30 py-10 text-xs font-mono text-gray-400">
                    <Users className="w-5 h-5" />
                    No one has entered yet — be the first.
                </div>
            ) : filtered.length === 0 ? (
                <p className="py-6 text-center text-xs font-mono text-gray-400">No entry matches “{query}”.</p>
            ) : (
                <ul className="max-h-80 overflow-y-auto pr-1 space-y-1 [scrollbar-width:thin]">
                    {filtered.map(p => {
                        const isYou = p.user_id === user?.id;
                        const isWinner = p.user_id === raffle.winner?.user_id;
                        const share = (p.tickets / total) * 100;
                        return (
                            <li
                                key={p.user_id}
                                className={`relative overflow-hidden rounded-lg px-3 py-2 ${
                                    isWinner ? 'bg-amber-500/10' : isYou ? 'bg-green-500/10' : 'bg-gray-800/30'
                                }`}
                            >
                                {/* Share of the pool as a faint fill behind the row */}
                                <div
                                    className={`absolute inset-y-0 left-0 ${isWinner ? 'bg-amber-400/10' : 'bg-emerald-400/[0.06]'}`}
                                    style={{ width: `${share}%` }}
                                />
                                <div className="relative flex items-center gap-2.5">
                                    <span className="w-7 shrink-0 text-[11px] font-mono text-gray-500 tabular-nums">#{p.rank}</span>
                                    {p.avatar ? (
                                        // eslint-disable-next-line @next/next/no-img-element
                                        <img src={p.avatar} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                                    ) : (
                                        <span className="w-6 h-6 rounded-full bg-gray-700/60 shrink-0 flex items-center justify-center text-[10px] font-bold text-gray-300 uppercase">
                                            {p.username?.[0] ?? '?'}
                                        </span>
                                    )}
                                    <span className={`min-w-0 flex-1 truncate text-sm font-mono ${isYou ? 'font-bold text-green-400' : 'text-gray-200'}`}>
                                        {p.username ?? 'Unknown'}{isYou && ' (you)'}
                                    </span>
                                    {isWinner && <Crown className="w-3.5 h-3.5 shrink-0 text-amber-300" />}
                                    <span className="shrink-0 text-right">
                                        <span className="block text-sm font-mono font-bold text-white tabular-nums">{p.tickets.toLocaleString()}</span>
                                        <span className="block text-[10px] font-mono text-gray-500 tabular-nums">{share < 0.1 ? '<0.1' : share.toFixed(1)}%</span>
                                    </span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
